"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FlvHttpPlayer = void 0;
const mobx_1 = require("mobx");
const adapter_1 = require("../../api/adapter");
const feature_1 = require("../../api/player/features/feature");
const errors_deprecated_1 = require("../errors-deprecated");
const core_1 = require("./core");
const browser_support_1 = require("../utils/browser-support");
const errors_1 = require("../errors");
class FlvHttpPlayer extends core_1.CorePlayer {
    static async isSupported(logger) {
        return (0, browser_support_1.supportsFlvHttp)(logger);
    }
    async isSupported() {
        return FlvHttpPlayer.isSupported();
    }
    static get format() {
        return "flv-http";
    }
    get format() {
        return FlvHttpPlayer.format;
    }
    constructor(ctx, provider, options) {
        super(ctx, provider, options);
        this.player = null;
        this.isFirstLoad = true;
        this.isStartingToPlay = false;
        this.isPauseQueued = false;
        this.addInnerDisposer((0, mobx_1.reaction)(() => { var _a; return [(_a = this.currentQuality) === null || _a === void 0 ? void 0 : _a.layer, this.internalPaused]; }, ([layer, internalPaused]) => {
            const hasLayerId = layer != null && typeof layer.id === "string" && layer.id !== "";
            if (hasLayerId && !internalPaused && !this.isStartingToPlay) {
                this.requestUrl(layer.id);
            }
        }));
        this.on("hostElementAttached", this.handleElAttached);
        this.addInnerDisposer(() => {
            this.destroyPlayer();
        });
    }
    async ready() {
        if (adapter_1.device.isImplements(adapter_1.Feature.MPEGTS)) {
            await adapter_1.device.loadMpegtsScript();
        }
        await super.ready();
    }
    pauseEl() {
        var _a;
        // if the video element is starting to play, we need to queue the pause to
        // to prevent a play() request was interrupted by a call to pause() error
        if (this.isStartingToPlay) {
            this.isPauseQueued = true;
            return;
        }
        super.pauseEl();
        // this is a third-party player, which manipulate with HTMLElement by itself
        // so we need to say it pause too
        (_a = this.player) === null || _a === void 0 ? void 0 : _a.pause();
    }
    handleSource(manifest) {
        if (this.suspended) {
            return;
        }
        const format = manifest === null || manifest === void 0 ? void 0 : manifest.formats["flv-http"];
        if (format == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("manifest doesn't contains 'flv-http' format", { manifest }));
        }
    }
    async requestUrl(url) {
        if (!adapter_1.device.isImplements(adapter_1.Feature.MPEGTS)) {
            this.throwErrorDeprecated(new errors_deprecated_1.PlayingIssueError("mpegts library is not loaded", { player: this }));
        }
        this.destroyPlayer();
        const authHeader = await this.getAuthHeader();
        const headers = { ...authHeader, ...this.options.requestHeaders };
        this.player = adapter_1.device.mpegts.createPlayer({
            type: "flv",
            isLive: true,
            url,
        }, { headers });
        this.player.on(adapter_1.device.mpegts.Events.ERROR, this.handleMpegtsError);
        if (this.elementSupervisor != null) {
            this.handleElAttached({ el: Reflect.get(this.elementSupervisor, "element") });
        }
    }
    handleElAttached(ev) {
        if (this.player != null) {
            this.player.attachMediaElement(ev.el);
            this.player.load();
            this.ctx.logger.debug("host element is attached and loaded");
            if (this.isFirstLoad && !this.options.autoPlay) {
                this.isFirstLoad = false;
                return;
            }
            try {
                if (this.internalPaused) {
                    this.ctx.logger.debug("pause");
                    this.player.pause();
                }
                else {
                    this.ctx.logger.debug("play");
                    this.isPauseQueued = false;
                    this.isStartingToPlay = true;
                    const playPromise = this.player.play();
                    (playPromise || Promise.resolve()).then(() => {
                        this.handleStartedToPlay();
                    });
                }
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("mpegts call failed in handleElAttached()", {
                    inner,
                    player: this,
                }));
            }
        }
    }
    handleStartedToPlay() {
        this.isStartingToPlay = false;
        if (this.isPauseQueued) {
            this.pauseEl();
        }
    }
    destroyPlayer() {
        if (this.player == null || !adapter_1.device.isImplements(adapter_1.Feature.MPEGTS)) {
            return;
        }
        this.player.pause();
        this.player.unload();
        this.player.detachMediaElement();
        this.player.off(adapter_1.device.mpegts.Events.ERROR, this.handleMpegtsError);
        this.player.destroy();
        this.player = null;
    }
    handleMpegtsError(errorType) {
        if (errorType === "NetworkError") {
            this.ctx.logger.error(`FLVHTTP ${errorType}`);
        }
        else {
            this.throwError((0, errors_1.createError)(errors_1.ErrorCode.PlaybackError, "Unable to play the stream due to an uncaught error", {}));
        }
    }
    get implementedFeatures() {
        return [feature_1.Feature.BITRATE_SWITCHING, feature_1.Feature.MUTED_AUTOPLAY];
    }
}
exports.FlvHttpPlayer = FlvHttpPlayer;
FlvHttpPlayer.displayName = "FlvHttpPlayer";
//# sourceMappingURL=flv-http.js.map